import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, Plus, RotateCcw, Trash2, Wrench } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Skeleton } from "./ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { api, errMsg } from "../lib/api";

/**
 * Phase 107 — "Fix & re-import": the failed / rejected / un-ticked rows of one batch in an editable grid.
 * Edit PIN, DATE, RECEIPT, CHQ NO, NET AMT RECEIVED, PMT MODE inline (or drop a row), then
 * "Validate & preview" runs the normal import validation and hands the preview to the wizard,
 * where rows are ticked and confirmed exactly like a file upload.
 */
const FIELDS = [
  ["pin", "PIN", "w-32 font-mono uppercase"], ["date", "DATE", "w-28"], ["receipt", "RECEIPT", "w-36 font-mono"],
  ["cheque_no", "CHQ NO", "w-28"], ["amount", "NET AMT RECEIVED", "w-28 text-right tabular-nums"], ["mode", "PMT MODE", "w-24"],
];
const STATUS_CLS = {
  skipped: "bg-destructive/10 text-destructive border-destructive/20",
  failed: "bg-destructive/10 text-destructive border-destructive/20",
  deselected: "bg-muted text-muted-foreground border-border",
};

export function FixRowsDialog({ code, batch, open, onOpenChange, onPreview, testId = "fix-rows" }) {
  const batchId = batch?.id;
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !batchId) return;
    let cancelled = false;
    setData(null); setRows([]); setError(null); setBusy(false);
    api.get(`/t/${code}/payment-import/${batchId}/failed-rows`)
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        setRows((r.data.rows || []).map((x, i) => ({ ...x, _key: `${x.row_number}-${i}`, _drop: false, amount: x.amount === "" || x.amount == null ? "" : String(x.amount) })));
      })
      .catch((e) => { if (!cancelled) setError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [open, code, batchId]);

  const kept = useMemo(() => rows.filter((r) => !r._drop), [rows]);
  const edit = (key, field, value) => setRows((rs) => rs.map((r) => (r._key === key ? { ...r, [field]: value } : r)));
  const toggleDrop = (key) => setRows((rs) => rs.map((r) => (r._key === key ? { ...r, _drop: !r._drop } : r)));
  const addRow = () => setRows((rs) => [...rs, { _key: `new-${Date.now()}`, row_number: null, status: "new", status_label: "New row", reason: "",
    pin: "", date: "", receipt: "", cheque_no: "", amount: "", mode: "", _drop: false }]);

  const validate = async () => {
    const payload = kept.map((r) => ({ pin: r.pin, date: r.date, receipt: r.receipt, cheque_no: r.cheque_no, amount: r.amount, mode: r.mode }))
      .filter((r) => Object.values(r).some((v) => String(v || "").trim()));
    if (payload.length === 0) return toast.error("Keep at least one row to re-import");
    setBusy(true);
    setError(null);
    try {
      const r = await api.post(`/t/${code}/payment-import/retry-preview`, { scope: batch?.scope || data?.scope || "main", parent_import_id: batchId, rows: payload });
      const s = r.data.summary || {};
      if (s.error_rows > 0) toast.warning(`${s.valid_rows} row(s) ready, ${s.error_rows} still locked — review the preview`);
      else toast.success(`${s.valid_rows} row(s) validated — review and confirm`);
      onPreview?.(r.data);
      onOpenChange(false);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Wrench className="h-4 w-4" /> Fix & re-import failed rows</DialogTitle>
          <DialogDescription className="break-all" data-testid={`${testId}-file`}>
            {batch?.filename || data?.filename || "Payment import"} — edit the cells, drop rows you don't want, then validate. Nothing is recorded until you confirm the preview.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" data-testid={`${testId}-error`}>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Could not continue</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {!data && !error && <div className="space-y-2" data-testid={`${testId}-loading`}><Skeleton className="h-8" /><Skeleton className="h-8" /><Skeleton className="h-8" /></div>}

        {data && (
          <>
            {data.retry_import_ids?.length > 0 && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid={`${testId}-retried-note`}>
                <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success))]" /> This batch already has {data.retry_import_ids.length} fix batch{data.retry_import_ids.length === 1 ? "" : "es"} — rows fixed earlier may show as duplicates.
              </p>
            )}
            {!data.has_inputs && rows.length > 0 && (
              <Alert data-testid={`${testId}-no-inputs`}>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Older batch</AlertTitle>
                <AlertDescription>The original cell values were not stored for this batch — only the PINs are known. Fill in the other columns before validating.</AlertDescription>
              </Alert>
            )}
            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid={`${testId}-empty`}>No failed or skipped rows in this batch.</p>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <Table data-testid={`${testId}-table`}>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12 text-xs">Row</TableHead>
                      {FIELDS.map(([k, label]) => <TableHead key={k} className="text-xs whitespace-nowrap">{label}</TableHead>)}
                      <TableHead className="text-xs min-w-[220px]">Why it failed</TableHead>
                      <TableHead className="w-10" aria-label="Remove" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r._key} className={r._drop ? "opacity-40" : ""} data-testid={`${testId}-row-${r.row_number ?? r._key}`}>
                        <TableCell className="text-xs tabular-nums text-muted-foreground align-top pt-3">{r.row_number ?? "new"}</TableCell>
                        {FIELDS.map(([k, , cls]) => (
                          <TableCell key={k} className="p-1.5 align-top">
                            <Input value={r[k] ?? ""} onChange={(e) => edit(r._key, k, e.target.value)} disabled={r._drop || busy}
                              className={`h-8 text-xs ${cls}`} aria-label={k} data-testid={`${testId}-cell-${k}-${r.row_number ?? r._key}`} />
                          </TableCell>
                        ))}
                        <TableCell className="align-top pt-2">
                          <Badge variant="outline" className={`text-[10px] font-normal mb-1 ${STATUS_CLS[r.status] || "bg-primary/10 text-primary border-primary/20"}`}>{r.status_label}</Badge>
                          <p className="text-[11px] text-muted-foreground leading-snug" data-testid={`${testId}-reason-${r.row_number ?? r._key}`}>{r.reason || "—"}</p>
                        </TableCell>
                        <TableCell className="p-1.5 align-top">
                          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleDrop(r._key)} disabled={busy}
                            aria-label={r._drop ? "Keep row" : "Drop row"} data-testid={`${testId}-drop-${r.row_number ?? r._key}`}>
                            {r._drop ? <RotateCcw className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Button type="button" variant="ghost" size="sm" onClick={addRow} disabled={busy} data-testid={`${testId}-add-row`}><Plus className="h-3.5 w-3.5 mr-1" /> Add a row</Button>
              <p className="text-xs text-muted-foreground" data-testid={`${testId}-kept-count`}>{kept.length} of {rows.length} row{rows.length === 1 ? "" : "s"} will be validated · DATE as DD-MM-YYYY · PMT MODE: Cash, Cheque, DD, UPI, Online, Card, POS</p>
            </div>
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button onClick={validate} disabled={busy || !data || kept.length === 0} data-testid={`${testId}-validate`}>
            <CheckCircle2 className="h-4 w-4 mr-2" /> {busy ? "Validating…" : `Validate & preview ${kept.length} row${kept.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default FixRowsDialog;
